import { createSelector } from "@reduxjs/toolkit";
import filter from "lodash/filter";
import forEach from "lodash/forEach";
import groupBy from "lodash/groupBy";
import isNil from "lodash/isNil";
import last from "lodash/last";
import map from "lodash/map";
import orderBy from "lodash/orderBy";
import range from "lodash/range";
import round from "lodash/round";
import sum from "lodash/sum";
import sumBy from "lodash/sumBy";
import uniqBy from "lodash/uniqBy";
import numeral from "numeral";

import { type RootState } from "@/rootReducer";

import {
  Adder,
  BatteryPurpose,
  ConsumptionProfileSettings,
  ConsumptionRules,
  CustomAdder,
  Discount,
  EnerfloProposalData,
  type Language,
  LenderRate,
} from "../types";
import { formatKw } from "./utils";

const MONTHS_IN_YEAR = 12;

type ActiveAdders = (Adder | CustomAdder)[];
type ActiveDiscounts = Discount[];

export const getTemplate = (state: RootState) => state.template.data!;
const getTemplateState = (state: RootState) => state.template!;
const getProposalState = (state: RootState) => state.proposal!;
const getBatterySizerState = (state: RootState) => state.batterySizer!;
const getDesignToolState = (state: RootState) => state.designTool!;

export const getProposal = createSelector(getProposalState, proposalState => proposalState.data!);
export const getProposalError = createSelector(getProposalState, proposalState => proposalState.error);
export const getTemplateError = createSelector(getTemplateState, templateState => templateState.error);
export const getProposalLoading = createSelector(getProposalState, proposalState => proposalState.isLoading);
export const getReportHash = createSelector(getProposalState, proposalState => proposalState.reportHash!);
export const getProposalPdfInfo = createSelector(getProposalState, proposalState => proposalState.pdfInfo);

export const getSavingsAnalysis = createSelector(getProposal, state => state.savingsAnalysis);

export const getForecast = createSelector(getProposal, state => state.forecast);

export const getForecastFirstYearAfter = createSelector(getForecast, forecast => forecast?.after.annual[0]);

export const getIsApplyingForFinancing = createSelector(
  [getProposalState],
  proposalState => proposalState.applyingForFinancing
);

export const getLanguage = createSelector(getTemplate, template =>
  (template.language || "en-US").replace("en-US", "en")
);
export const getSalesCompanyLogo = createSelector(getTemplate, template => template.logo);
export const getInstallerLogo = createSelector(getTemplate, template => template.installerLogo);
export const getCompanyInfo = createSelector(getTemplate, template => template.companyInfo);
export const getOfficeInfo = createSelector(getTemplate, template => template.officeInfo);
export const getShowEquipmentPricing = createSelector(getTemplate, template => template.showEquipmentPricing || []);

export const getV2DealId = createSelector(getProposal, proposal => proposal.dealId);
export const getIsV2Deal = createSelector(getV2DealId, v2DealId => !!v2DealId);
export const getCustomer = createSelector(getProposal, proposal => proposal.customer);
export const selectProjectAddress = createSelector(getProposal, proposal => proposal.customer.address);
export const getSalesRep = createSelector(getProposal, proposal => proposal.agent);
export const getShareLink = createSelector(getProposal, proposal => proposal.shareLink);
export const getProposalPdfLink = createSelector(getProposal, proposal => proposal.proposalPdfLink);
export const getUtilityCompany = createSelector(getProposal, proposal => ({
  name: proposal.utilityCompany,
  rateSchedule: proposal.utilityRateSchedule,
}));
export const getTariffName = createSelector(getProposal, proposal => proposal.tariffName);
export const getDisplayLogo = createSelector(getTemplate, template => template.logo?.displayLogo ?? true);
export const getApplyNowUrl = createSelector([getProposal], proposal => proposal.links?.applyNow);
export const getDealUrl = createSelector(
  getProposal,
  proposal => proposal.links?.dealUrl ?? `/surveys/${proposal.surveyId}`
);
export const getPrintUrl = createSelector(getReportHash, reportHash => `/proposals/${reportHash}?print-pdf`);
export const getSlideshowUrl = createSelector(getReportHash, reportHash => `/proposals/${reportHash}`);
export const getRedesignUrl = createSelector(getProposal, proposal => `/deal/redesign/${proposal.surveyId}`);
export const getDesignsUrl = createSelector(getProposal, proposal => `/deals/${proposal.dealId}?stage=designs`);
export const getIsNonIntegratedDesign = createSelector(getProposal, proposal => proposal.design.tool === "other");
export const getIsSoloDesign = createSelector(getProposal, proposal => proposal.design.tool === "solo");
export const getIsAerialyticDesign = createSelector(getProposal, proposal => proposal.design.tool === "aerialytic");
export const getIsScaniflyDesign = createSelector(getProposal, proposal => proposal.design.tool === "scanifly");
export const getIsSolargrafDesign = createSelector(getProposal, proposal => proposal.design.tool === "solargraf");
export const getIsSourcedFromDesignTool = createSelector(getProposal, proposal =>
  ["aerialytic", "AuroraDTP", "solargraf", "scanifly"].includes(proposal.design.tool)
);

export const getHasArrayData = createSelector(getProposal, proposal => !!proposal.design.arrays);
export const getUtilityBillFiles = createSelector(getProposal, proposal => proposal.utilityBillFiles ?? []);
export const getEnvironmentalImpactStats = createSelector(getProposal, proposal => proposal.environmentalImpact);
export const getProposalDeal = createSelector(getProposal, proposal => proposal?.deal);
export const getProposalStatus = createSelector(getProposal, proposal => proposal?.status ?? {});
export const getProposalLinks = createSelector(getProposal, proposal => proposal?.links ?? {});
export const getLenderCap = createSelector(getProposal, proposal => proposal.lenderCap);
export const getIsOptimizingDesign = createSelector(
  getProposalState,
  proposalState => proposalState.isOptimizingDesign
);
export const getIsRecalculatingPrice = createSelector(
  getProposalState,
  proposalState => proposalState.isRecalculatingPrice
);

export const getIsProposalLocked = createSelector(
  [getProposalState, getProposalStatus],
  (proposalState, proposalStatus) => {
    return !!(proposalState.locked || proposalStatus.isLocked);
  }
);
export const getOptimizeError = createSelector(getProposalState, proposalState => {
  return proposalState.optimizeError;
});
export const getRecalculatePricingError = createSelector(getProposalState, proposalState => {
  return proposalState.recalculatePricingError;
});

export const getShouldPreventUpdates = createSelector(getProposalStatus, status => !!status.isLocked);

export const getDealLockStatus = createSelector(
  [getProposalDeal, getProposalStatus, getProposalLinks],
  (deal, status, links) => {
    const { hasSignedAgreement, hasApprovedFinancing } = status ?? {};

    const hasSelectedProposal = !!deal?.selectedProposalId;

    if (hasSignedAgreement) {
      return {
        description:
          "There is a signed agreement with this proposal. Pricing cannot be modified unless the agreement is voided.",
        actionButtonText: "View Agreement",
        actionButtonUrl: links.contractPreview,
      };
    } else if (hasApprovedFinancing) {
      return {
        description:
          "There is an approved loan application for this customer. Pricing cannot be modified unless the application is voided.",
        actionButtonText: "View Application",
        actionButtonUrl: links.financeAppStatus,
      };
    } else if (hasSelectedProposal) {
      return {
        description:
          "There is a selected proposal for this deal. Pricing cannot be modified unless the proposal is unselected.",
        actionButtonText: "View Proposal",
        actionButtonUrl: links.dealUrl ? `${links.dealUrl}?stage=proposal` : undefined,
      };
    }

    return null;
  }
);

export const getDealBanners = createSelector(getProposalDeal, deal => deal?.dealBanners ?? []);

/**
 * TPO
 */
export const getFinanceMethod = createSelector(getProposal, proposal => proposal.financeMethod);
export const getIsTpo = createSelector(getProposal, proposal => proposal.financeMethod === "tpo");

/**
 * Abilities
 */

const getAbilities = createSelector(getProposal, proposal => proposal.abilities);
const getLoanProductOptionsFromProposal = createSelector(getProposal, proposal => proposal.loanProducts);

export const canUserEditProposal = createSelector(
  [getProposal, (state: RootState) => state.deck.features.printMode],
  (proposal, isPrintMode) => !isPrintMode && !!proposal && !proposal.readonly
);

export const canUserEditFinanceProduct = createSelector(
  [canUserEditProposal, getLoanProductOptionsFromProposal],
  (canEdit, loanProducts) => canEdit && loanProducts && loanProducts.length > 0
);

export const canToggleAdderCostPpw = createSelector(getAbilities, abilities => abilities.canToggleAdderCostPpw);
export const canAddCustomAdders = createSelector(getAbilities, abilities => abilities.canAddCustomAdders);
export const canAddCustomDiscounts = createSelector(getAbilities, abilities => abilities.canAddCustomDiscounts);
export const canUserManageDiscounts = createSelector(getAbilities, abilities => abilities.canAddDiscounts);
export const canUserAdjustTaxCredit = createSelector(
  [canUserEditProposal, getAbilities],
  (canEdit, abilities) => canEdit && abilities.canAdjustTaxCredit !== false
);
export const canUserEditGrossPrice = createSelector(getAbilities, abilities => abilities.editGrossPrice);
export const canUserUnlockProposal = createSelector(
  [canUserEditProposal, getProposalStatus],
  (canEdit, status) => canEdit && !status.isLocked
);
export const canShare = createSelector(getAbilities, abilities => !!abilities.canShare);
export const getCanChangeBatteries = createSelector(getAbilities, abilities => !!abilities.canChangeBatteries);
export const getCanUserUpdateBatteries = createSelector(
  [canUserEditProposal, getCanChangeBatteries, getProposalStatus],
  (canEdit, canChangeBatteries, status) => canEdit && canChangeBatteries && !status.isLocked
);
export const getCanChangeInverter = createSelector(getAbilities, abilities => !!abilities.canChangeInverter);
export const getCanChangeRateSchedule = createSelector(getAbilities, abilities => !!abilities.canChangeRateSchedule);

export const canOptimizeByProduction = createSelector(getAbilities, abilities =>
  isNil(abilities.canOptimizeByProduction) ? true : !!abilities.canOptimizeByProduction
);
export const canOptimizeByOffset = createSelector(getAbilities, abilities =>
  isNil(abilities.canOptimizeByOffset) ? true : !!abilities.canOptimizeByOffset
);
export const canOptimizeByPayment = createSelector(getAbilities, abilities =>
  isNil(abilities.canOptimizeByPayment) ? true : !!abilities.canOptimizeByPayment
);
export const canChangePanelCount = createSelector(getAbilities, abilities =>
  isNil(abilities.canChangePanelCount) ? true : !!abilities.canChangePanelCount
);
export const canOptimizeByConsumption = createSelector(getAbilities, abilities =>
  isNil(abilities.canOptimizeByConsumption) ? true : !!abilities.canOptimizeByConsumption
);
export const canOptimizeByMonthlyConsumption = createSelector(getAbilities, abilities =>
  isNil(abilities.canOptimizeByMonthlyConsumption) ? true : !!abilities.canOptimizeByMonthlyConsumption
);
export const canOptimizeBySystemCost = createSelector(getAbilities, abilities =>
  isNil(abilities.canOptimizeBySystemCost) ? true : !!abilities.canOptimizeBySystemCost
);
export const canOptimizeBySystemSize = createSelector(getAbilities, abilities =>
  isNil(abilities.canOptimizeBySystemSize) ? true : !!abilities.canOptimizeBySystemSize
);

export const getCanChangePanel = createSelector(getAbilities, abilities => !!abilities.canChangePanel);

export const getCanOptimizeByMetric = createSelector(
  [
    canOptimizeByProduction,
    canOptimizeByOffset,
    canOptimizeByPayment,
    canChangePanelCount,
    canOptimizeByConsumption,
    canOptimizeByMonthlyConsumption,
    canOptimizeBySystemCost,
    canOptimizeBySystemSize,
  ],
  (production, offset, payment, panelCount, consumption, monthlyConsumption, systemCost, systemSize) => {
    return {
      production,
      offset,
      payment,
      panelCount,
      consumption,
      monthlyConsumption,
      systemCost,
      systemSize,
    };
  }
);

/**
 * Settings
 */

export const getUseSimplifiedProductionCalcs = createSelector(
  getTemplate,
  template => template.companyInfo.useSimplifiedProductionCalcs
);

export const getDesignToolData = createSelector(getDesignToolState, designTool => designTool.data);

export const getIntegratedPluginStatus = createSelector(
  getDesignToolState,
  designTool => designTool.integratedPluginStatus
);

/**
 * Design
 */
export const getDesignData = createSelector(getProposal, proposal => proposal.design);

export const getBatteries = createSelector(getDesignData, design => design.batteries ?? []);
export const getHasOnlyBatteries = createSelector(
  getDesignData,
  design => design.arrays?.length === 0 && design.batteries.length > 0 && !design.inverterId
);
export const getBatteryPurpose = createSelector(
  getDesignData,
  (design): BatteryPurpose => design.designBatteryDetails?.purpose ?? "backup"
);
export const getBatteriesTotalCapacity = createSelector(getBatteries, batteries => sumBy(batteries, "capacity"));
export const getMinPanelCount = createSelector(getDesignData, design => design.minPanelCount);
export const getMaxPanelCount = createSelector(getDesignData, design => design.maxPanelCount);
export const getPanelCount = createSelector(getDesignData, design => design.panelCount);
export const getPanelName = createSelector(getDesignData, design => design.panelName);
export const getPanelWatts = createSelector(getDesignData, design => design.panelWatts);
export const getSystemSizeInWatts = createSelector(getDesignData, design => design.systemSize ?? 0);
export const getSystemSizeInKw = createSelector(getSystemSizeInWatts, systemSize => formatKw(systemSize));
export const getEstimatedYearlyConsumption = createSelector(getDesignData, design => design.estimatedConsumption);
export const getEstimatedMonthlyConsumption = createSelector(
  getDesignData,
  design => design.estimatedMonthlyConsumption
);
export const getEstimatedYearlyProduction = createSelector(getDesignData, design => design.annualProduction);
export const getRoofImageUrl = createSelector(getDesignData, design => design.roofImageUrl);
export const getHeatMapOffImageUrl = createSelector(getDesignData, design => design.roofHeatMapOffImageUrl);
export const getSolarOffset = createSelector(getDesignData, design => design.solarOffset);
export const getInverterName = createSelector(getDesignData, design => design.inverterName);
export const getInverterId = createSelector(getDesignData, design => design.inverterId.toString());
export const getInverterManufacturerName = createSelector(getDesignData, design => design.inverterManufacturerName);
export const getHasBattery = createSelector(getDesignData, design => design.batteryName !== null);

export const getBatteryOptions = createSelector(getProposal, proposal => proposal.batteryOptions);
export const getPanelOptions = createSelector(getProposal, proposal => proposal.panelOptions);
export const getInverterOptions = createSelector(getProposal, proposal => proposal.inverterOptions);
export const getFullPricingBreakdown = createSelector(getProposal, proposal => proposal.pricingBreakdown);
export const getArrayData = createSelector(getDesignData, design => design.arrays);
export const getDesignErrors = createSelector(getProposal, proposal => proposal?.design?.errors || []);

/**
 * Battery Sizer
 */

export const getHasFetchedInitialBatteryRecommendations = createSelector(
  getBatterySizerState,
  sizer => sizer.hasFetchedInitial
);
export const getBatterySizerRecommendations = createSelector(getBatterySizerState, sizer => sizer.recommendations);
export const getBatterySizerEnergyBreakdown = createSelector(getBatterySizerState, sizer => sizer.energyBreakdown);
export const getBatterySizerIdealCapacity = createSelector(getBatterySizerState, sizer => sizer.idealCapacity);
export const getBatterySizerError = createSelector(getBatterySizerState, sizer => sizer.error);
export const getBatterySizerLoadingRecommendations = createSelector(
  getBatterySizerState,
  sizer => sizer.loadingRecommendations
);
export const getBatterySizerIsLoading = createSelector(
  [getBatterySizerLoadingRecommendations, getHasFetchedInitialBatteryRecommendations, getBatterySizerError],
  (isLoadingRecommendations, hasFetchedInitial, hasError) =>
    isLoadingRecommendations || (!hasFetchedInitial && !hasError)
);
export const getBatterySizerSettings = createSelector(getBatterySizerState, sizer => sizer.settings);
export const getShowSelectionPromptOverlay = createSelector(
  getBatterySizerState,
  sizer => sizer.showSelectionPromptOverlay
);
export const getBatterySizerStats = createSelector(
  getBatterySizerIdealCapacity,
  getBatterySizerEnergyBreakdown,
  (idealCapacity, breakdown) => {
    const dailyEnergyUse = sumBy(breakdown, "consumption");
    const systemSize = sumBy(breakdown, "production");

    return {
      dailyEnergyUse,
      idealCapacity,
      systemSize,
      dailyOffset: systemSize / dailyEnergyUse,
    };
  }
);
export const getActiveRecommendationLegendItems = createSelector(
  getBatterySizerState,
  sizer => sizer.activeLegendItems
);

/**
 * Consumption Profile
 */

export const canManageConsumptionProfiles = createSelector(
  getIsV2Deal,
  getAbilities,
  (isV2Deal, abilities) => isV2Deal && abilities.canManageConsumptionProfiles
);
export const getConsumptionProfileId = createSelector(getDesignData, design => design.consumptionProfileId);
export const getConsumptionProfiles = createSelector(getProposal, proposal => proposal.consumptionProfiles);
export const getConsumptionProfileSettings = createSelector(
  getProposal,
  (proposal): ConsumptionProfileSettings => proposal.settings?.consumptionProfiles ?? { rules: [] }
);
export const getConsumptionRules = createSelector(getConsumptionProfileSettings, settings => settings?.rules || []);
export const getIfConsumptionRuleIsEnabled = (rule: ConsumptionRules) =>
  createSelector(getConsumptionRules, rules => rules.includes(rule));

// These rules are used internally and should not be displayed to the user as requirements to fulfill a consumption profile
const internalConsumptionRules = ["allowRateEdit"];

export const getDisplayableConsumptionRules = createSelector(getConsumptionRules, rules =>
  rules.filter(setting => !internalConsumptionRules.includes(setting))
);

export const getConsumption = createSelector(getProposal, proposal => proposal.consumption);

/**
 * Utility Costs
 */

export const getUtilityCosts = createSelector(getProposal, proposal => proposal.utilityCosts);
export const getUtilityRateIncrease = createSelector(getUtilityCosts, utilityCosts => utilityCosts.rateIncrease || 3.5);

export const getCustomkwhRate = createSelector(
  getUtilityCosts,
  utilityCosts => parseFloat(`${utilityCosts.kwhRate}`) || 0
);

/**
 * Extract the first year's average monthly fixed costs.
 *
 * Historically there was no division between pre- and post-solar contexts, and when the post-solar
 * context was eventually added this value was still being sourced from the pre-solar consumption
 * estimate. Later, the full forecast was added to the payload and includes both pre-solar (before)
 * and post-solar (after) contexts.
 *
 * If the forecast exists in the payload, then fixed costs are taken from the first year of the
 * after context. If the forecast doesn't exist, then it uses the legacy behavior of extracting the
 * values from the `utilityCosts` block, which is pre-solar originating from the consumption
 * profile.
 */
export const getAverageMonthlyFixedCosts = createSelector(
  [getForecastFirstYearAfter, getUtilityCosts],
  (forecasted, utilityCosts) => {
    if (forecasted) {
      return round(forecasted.utility.cost.fixed / MONTHS_IN_YEAR, 2);
    }

    return utilityCosts.averageMonthlyFixedCosts || 0;
  }
);

/**
 * Extract the first year's average minimum monthly bill, if any.
 *
 * Historically there was no division between pre- and post-solar contexts, and when the post-solar
 * context was eventually added this value was still being sourced from the pre-solar consumption
 * estimate. Later, the full forecast was added to the payload and includes both pre-solar (before)
 * and post-solar (after) contexts.
 *
 * If the forecast exists in the payload, then minimum bills are taken from the first year of the
 * after context. If the forecast doesn't exist, then it uses the legacy behavior of extracting the
 * values from the `utilityCosts` block, which is pre-solar originating from the consumption
 * profile.
 */
export const getAverageMonthlyMinimumCosts = createSelector(
  [getForecastFirstYearAfter, getUtilityCosts],
  (forecasted, utilityCosts) => {
    if (forecasted) {
      if (forecasted.utility.cost.minimum) {
        return round(forecasted.utility.cost.minimum / MONTHS_IN_YEAR, 2);
      }

      return undefined;
    }

    return utilityCosts.averageMonthlyMinimumCosts;
  }
);

/**
 * Solar Costs
 */

export const getSolarCostData = createSelector(getProposal, proposal => proposal.solarCosts);

export const getMonthlyPaymentSchedule = createSelector(
  getSolarCostData,
  solarCosts => solarCosts.monthlyPaymentSchedule
);

export const getHasMonthlyPaymentEscalated = createSelector(
  [getSolarCostData],
  solarCosts => !!solarCosts.monthlyPaymentEscalated
);

export const getShowPostPaymentMessage = createSelector(
  [getSolarCostData],
  solarCosts => !!solarCosts.monthlyPaymentSchedule?.sans_buydown || !!solarCosts.paymentBreakdown
);

export const getShowAfterBuydownPayment = createSelector(
  getMonthlyPaymentSchedule,
  getHasMonthlyPaymentEscalated,
  getShowPostPaymentMessage,
  (monthlyPaymentSchedule, hasMonthlyPaymentEscalated, showPostPaymentMessage) => {
    return (!hasMonthlyPaymentEscalated && !showPostPaymentMessage) || monthlyPaymentSchedule?.intro.payment === 0;
  }
);

export const getSolarMonthlyPayment = createSelector(
  getSolarCostData,
  getMonthlyPaymentSchedule,
  getShowAfterBuydownPayment,
  (solarCosts, monthlyPaymentSchedule, showAfterBuydownPayment) => {
    if (monthlyPaymentSchedule) {
      if (showAfterBuydownPayment) {
        return monthlyPaymentSchedule.after_buydown.payment;
      }

      return monthlyPaymentSchedule.intro.payment;
    }

    return solarCosts.monthlyPayment;
  }
);

export const getCalculatedSystemCost = createSelector(getSolarCostData, x => x.systemCost);
export const getCalculatedSystemCostBeforeRebates = createSelector(getSolarCostData, x => x.systemCostBeforeRebates);
export const getSystemCostBreakdown = createSelector(getSolarCostData, x => x.systemCostBreakdown);
export const getDegradation = createSelector(getSolarCostData, x => x.degradation || 0);
export const getSummaryEquipmentCost = createSelector(
  getShowEquipmentPricing,
  getSystemCostBreakdown,
  (showPricingFor, systemCostBreakdown) => {
    if (!showPricingFor?.length || !systemCostBreakdown) {
      return 0;
    }

    const values: number[] = [];

    if (showPricingFor.includes("INVERTER")) {
      values.push(systemCostBreakdown.inverterTotal || 0);
    }

    if (showPricingFor.includes("BATTERY")) {
      values.push(systemCostBreakdown.batteryTotal || 0);
    }

    if (showPricingFor.includes("PV_MODULE")) {
      values.push(systemCostBreakdown.moduleTotal || 0);
    }

    return sum(values);
  }
);
export const getSummarySystemCost = createSelector(
  getShowEquipmentPricing,
  getCalculatedSystemCost,
  getCalculatedSystemCostBeforeRebates,
  getSystemCostBreakdown,
  (showPricingFor, systemCost, systemCostBeforeRebates, systemCostBreakdown) => {
    if (!showPricingFor?.length) {
      if (systemCostBreakdown?.showRebatesReducingSystemCostTotal && systemCostBeforeRebates) {
        return systemCostBeforeRebates;
      }

      return systemCost;
    }

    if (!systemCostBreakdown) {
      return systemCost;
    }

    const addersAndDealerFees =
      (systemCostBreakdown.showRebatesReducingSystemCostTotal
        ? systemCostBreakdown.addersAndDealerFeesBeforeRebates
        : systemCostBreakdown.addersAndDealerFees) || 0;
    const values = [addersAndDealerFees];

    if (!showPricingFor.includes("INVERTER")) {
      values.push(systemCostBreakdown.inverterTotal || 0);
    }

    if (!showPricingFor.includes("BATTERY")) {
      values.push(systemCostBreakdown.batteryTotal || 0);
    }

    if (!showPricingFor.includes("PV_MODULE")) {
      values.push(systemCostBreakdown.moduleTotal || 0);
    }

    return sum(values);
  }
);
export const getGrossSystemCost = createSelector(getSolarCostData, x => x.grossSystemCost);
export const getNeedFinancedAmount = createSelector(getSolarCostData, x => x.needsFinancing);
export const getLenderDisclaimer = createSelector(getSolarCostData, getLanguage, (x, language) =>
  x.lenderDisclaimerTranslations && x.lenderDisclaimerTranslations[language]
    ? x.lenderDisclaimerTranslations[language]
    : x.lenderDisclaimer
);
export const getGrossPpw = createSelector(getSolarCostData, x => x.ppwFull);
export const getNetPpw = createSelector(getSolarCostData, x => x.ppwNet);
export const getPpwAfterRebates = createSelector(getSolarCostData, x => x.ppwAfterRebates);
export const getNetSystemCost = createSelector(getSolarCostData, x => x.netSystemCost);
export const getRebates = createSelector(getSolarCostData, x => x.rebates ?? []);
export const getPPAEscalator = createSelector(getSolarCostData, x => x.escalator);
export const getTpoEscalator = createSelector(getSolarCostData, x => x.tpo?.escalator || 0);
export const getEscalator = createSelector(
  [getPPAEscalator, getTpoEscalator, getIsTpo],
  (escalator, tpoEscalator, isTpo) => (isTpo ? tpoEscalator : escalator)
);
export const getPpaLeaseOption = createSelector(getSolarCostData, x => x.tpoPrPpaLeaseOptions);
export const getTpoQuestionOptions = createSelector(getSolarCostData, x => x.tpoQuestions);
export const getEscalatorPricePerkWh = createSelector(getSolarCostData, x => x.tpoPrPpaLeaseOptions);
export const getPpaMonthlyPayment = createSelector(getSolarCostData, x => x.monthlyPayment);
export const getTpoPriceSelect = createSelector(getSolarCostData, x => x.tpoPriceSelect);
export const getIsFinanceApplicationRequired = createSelector(getSolarCostData, x => !!x.isFinanceApplicationRequired);
export const getTpoSettings = createSelector(
  getSolarCostData,
  x => x.tpo ?? ({} as Partial<EnerfloProposalData["solarCosts"]["tpo"]>)
);
export const getTpoRate = createSelector(getSolarCostData, x => x.tpo?.rate);
export const getDeferredPaymentType = createSelector(getSolarCostData, x => x.customFields?.deferredPaymentType);
export const getDeferredPaymentPercentage = createSelector(
  getSolarCostData,
  x => x.customFields?.deferredPaymentPercentage
);

/**
 * Equipment
 */

// All equipment added to this proposal (prices here do not include dealer fees)
export const getEquipment = createSelector(getFullPricingBreakdown, pricingBreakdown =>
  orderBy(pricingBreakdown?.equipment || [], "name")
);

const getEquipmentDisplayableToCustomer = createSelector(getSolarCostData, x => orderBy(x.equipment || [], "name"));

// Equipment that is permitted to be displayable on pricing breakdowns based on
// the showEquipmentPricing template setting (prices here include dealer fees)
export const getEquipmentBreakdown = createSelector(
  getShowEquipmentPricing,
  getEquipmentDisplayableToCustomer,
  (showPricingFor, equipment) =>
    equipment.filter(item => !showPricingFor?.length || (item.type && showPricingFor.includes(item.type)))
);

/**
 * Adders
 */

export const getAvailableAdders = createSelector(getSolarCostData, solarCosts =>
  orderBy(solarCosts.availableAdders ?? solarCosts.adders, "name")
);
export const getProposalAdders = createSelector(getSolarCostData, solarCosts =>
  orderBy(solarCosts.proposalAdders ?? solarCosts.adders, "name")
);
export const getCustomAdder = createSelector(getSolarCostData, solarCosts => solarCosts.customAdder);

export const getSystemAddersTotal = createSelector(getSolarCostData, solarCosts => solarCosts.systemAdders);

const getActiveAdders = createSelector(getProposalAdders, adders => filter(adders, "active"));
export const getAllActiveAdders = createSelector([getActiveAdders, getCustomAdder], (activeAdders, customAdder) => {
  return ([...activeAdders, customAdder] as ActiveAdders).filter(Boolean);
});
export const getAddersBreakdown = createSelector(getFullPricingBreakdown, pricingBreakdown => {
  return pricingBreakdown?.adders || [];
});

export const getHideValueAddersPriceFromCustomer = createSelector(
  getSolarCostData,
  solarCosts => solarCosts.hideAddersCustomer
);

/**
 * Discounts
 */

export const getDiscountOptions = createSelector(getSolarCostData, solarCosts =>
  orderBy(solarCosts.discounts ?? [], "name")
);
export const getCustomDiscount = createSelector(getSolarCostData, solarCosts => solarCosts.customDiscount);
export const getActiveDiscountIds = createSelector(getSolarCostData, solarCosts =>
  map(solarCosts.activeDiscounts, "id").filter(Boolean)
);
const getActiveDiscountsMap = createSelector(getSolarCostData, solarCosts => {
  const discountsMap: Record<string | number, Discount> = {};

  if (solarCosts.activeDiscounts) {
    for (let discount of solarCosts.activeDiscounts) {
      const id = discount.id;
      if (id) {
        discountsMap[id] = discount;
      }
    }
  }

  return discountsMap;
});

const getActiveDiscounts = createSelector(
  [getDiscountOptions, getActiveDiscountsMap],
  (discountOptions, activeDiscountsMap) => {
    return discountOptions.map(discountOption => {
      if (!discountOption.id) return undefined;
      const activeDiscount = activeDiscountsMap[discountOption.id];
      if (!activeDiscount) return undefined;
      // merge properties from the base discount and the active discount overrides
      return {
        ...discountOption,
        ...activeDiscount,
      };
    });
  }
);

export const getAllActiveDiscounts = createSelector(
  [getActiveDiscounts, getCustomDiscount],
  (activeDiscounts, customDiscount) => filter([...activeDiscounts, customDiscount] as ActiveDiscounts, x => !!x)
);

const getLanguageValueOrFallback = <V>(
  languageKey: Language,
  registry: Partial<Record<Language, V>>
): V | undefined => {
  return registry[languageKey] || registry["en-US"];
};

export const getPaymentBreakdown = createSelector(getSolarCostData, solarCosts => {
  if (!solarCosts.paymentBreakdown) return undefined;

  return solarCosts.paymentBreakdown.map(payment => {
    return {
      en: payment["en-US"],
      es: getLanguageValueOrFallback("es", payment),
      ...payment,
    };
  });
});

export const getIntroPaymentSchedule = createSelector(getSolarCostData, solarCosts => {
  if (!solarCosts.introPaymentSchedule) return undefined;

  return {
    en: solarCosts.introPaymentSchedule["en-US"],
    es: getLanguageValueOrFallback("es", solarCosts.introPaymentSchedule),
    ...solarCosts.introPaymentSchedule,
  };
});

export const getHideTaxCreditMessage = createSelector(getSolarCostData, solarCosts => {
  return !!solarCosts.hideTaxCreditMessage;
});

export const getTaxCreditMessage = createSelector(getSolarCostData, solarCosts => {
  if (!solarCosts.taxCreditMessage) return undefined;

  return {
    en: solarCosts.taxCreditMessage["en-US"],
    es: getLanguageValueOrFallback("es", solarCosts.taxCreditMessage),
    ...solarCosts.taxCreditMessage,
  };
});

export const getDeferredPeriod = createSelector(getSolarCostData, solarCosts => solarCosts.deferredPeriod);

export const getDiscountsTotal = createSelector(getSolarCostData, solarCosts => solarCosts.discountTotal ?? 0);

//
// Design Portal
//
const getDesignPortalState = (state: RootState) => state.designPortal!;

export const getArrayDataTableExpanded = createSelector(
  getDesignPortalState,
  designPortal => designPortal.arrayDataTableExpanded
);

/**
 * Rebates and Credits
 */

export const getAppliedTaxCredits = createSelector(getSolarCostData, x => x.creditsApplied ?? 0);
export const getTotalAppliedCredits = createSelector(getSolarCostData, x => x.totalCreditsApplied ?? 0);
export const getAvailableTaxCredits = createSelector(getSolarCostData, x => x.creditsAvailable ?? 0);
export const getPaydownApplied = createSelector(getSolarCostData, x => x.paydownApplied ?? 0);
export const getPercentTaxCredits = createSelector(getSolarCostData, x => x.creditsPercent ?? 0);
export const getFederalTaxCredit = createSelector(getSolarCostData, x => x.fedTaxCredit ?? 0);

export type RebateOrCredit = {
  name: string;
  value: number;
};

export const getAllNetRebatesAndCredits = createSelector([getFederalTaxCredit, getRebates], (federalItc, rebates) => {
  const rebatesOrCredits: RebateOrCredit[] = [];

  if (!!federalItc) {
    rebatesOrCredits.push({
      name: "Federal Tax Credit",
      value: federalItc,
    });
  }

  forEach(rebates, rebate => {
    if (!rebate.applyToNetCost) return;

    rebatesOrCredits.push({
      name: rebate.name,
      value: parseFloat(`${rebate.amount}`),
    });
  });

  return orderBy(rebatesOrCredits, "name");
});

export const getAllGrossRebatesAndCredits = createSelector(getRebates, rebates => {
  const grossCostRebates: RebateOrCredit[] = [];

  forEach(rebates, rebate => {
    if (rebate.applyToLoanAmount)
      grossCostRebates.push({
        name: rebate.name,
        value: parseFloat(`${rebate.amount}`),
      });
  });

  return grossCostRebates;
});

export const getGrossCostRebatesTotal = createSelector(getRebates, rebates => {
  let grossCostRebateAmount = map(rebates, rebate => (rebate.applyToLoanAmount ? parseFloat(`${rebate.amount}`) : 0));
  return sum(grossCostRebateAmount);
});

export const getNetRebatesAndCreditsTotal = createSelector([getFederalTaxCredit, getRebates], (federalItc, rebates) => {
  const allRebateAmounts = map(rebates, rebate => (rebate.applyToNetCost ? parseFloat(`${rebate.amount}`) : 0));
  const totalRebateAmount = sum(allRebateAmounts);
  const totalRebatesCredits = federalItc + totalRebateAmount;
  return totalRebatesCredits;
});

export const getAllSystemCostRebates = createSelector(getRebates, rebates => {
  const systemCostRebates: RebateOrCredit[] = [];

  forEach(rebates, rebate => {
    if (rebate.applyToSystemCost)
      systemCostRebates.push({
        name: rebate.name,
        value: parseFloat(`${rebate.amount}`),
      });
  });

  return systemCostRebates;
});

export const getRebatesReducingSystemCostTotal = createSelector(
  getSystemCostBreakdown,
  systemCostBreakdown => systemCostBreakdown?.rebatesReducingSystemCostTotal || 0
);

export const getShowRebatesReducingSystemCostTotal = createSelector(
  getSystemCostBreakdown,
  systemCostBreakdown => systemCostBreakdown?.showRebatesReducingSystemCostTotal || false
);

/**
 * Lender Options
 */

const getLoanProductOptionsFromAPI = createSelector(getProposal, proposal => []); // todo
const getSelectedLenderRate = createSelector(getSolarCostData, solarCosts => solarCosts.lenderRate);

export const getIsLeaseOrPpa = createSelector(getSelectedLenderRate, lenderRate => !!lenderRate?.lease);
export const getIsPpa = createSelector(
  [getSelectedLenderRate, getTpoSettings],
  (rate, tpo) => tpo?.type === "ppa" || !!rate?.code.includes("PPA")
);

export const getAllPossibleLenders = createSelector(
  [getLoanProductOptionsFromProposal, getLoanProductOptionsFromAPI],
  (loanProductsFromProposal, loanProductsFromApi) => {
    if (loanProductsFromProposal?.length) {
      return uniqBy(
        loanProductsFromProposal.map(product => product.lender),
        lender => lender.id
      );
    }

    return loanProductsFromApi ?? [];
  }
);

export const getLenderRateOptionsMap = createSelector(
  getLoanProductOptionsFromProposal,
  getIsV2Deal,
  (loanProducts, isV2Deal): Record<number, undefined | LenderRate[]> => {
    if (isV2Deal) {
      return groupBy(loanProducts, x => x.lender.id);
    }

    return groupBy(orderBy(loanProducts, "code"), x => x.lender.id);
  }
);

interface CurrentFinanceInfo {
  id?: number;
  name?: string;
  isCash?: boolean;
  logo?: string;
}

export const getCurrentFinanceInfo = createSelector(
  getSolarCostData,
  solarCosts =>
    ({
      id: solarCosts.lenderId,
      name: solarCosts.lenderName,
      isCash: solarCosts.lenderName === "Cash",
      logo: solarCosts.lenderLogo,
    }) as CurrentFinanceInfo
);
export const getCurrentLenderRateInfo = createSelector(getSolarCostData, solarCosts => ({
  id: solarCosts.lenderRateId,
}));

export const getDownPayment = createSelector(getSolarCostData, solarCosts => solarCosts.downPayment ?? 0);
export const getDownPaymentConditions = createSelector(
  getSolarCostData,
  solarCosts => solarCosts.downPaymentConditions ?? null
);
export const getLoanTermYears = createSelector(getSolarCostData, solarCosts => solarCosts.lenderRate?.term_years);
export const getSelectedLoanProductName = createSelector(getSolarCostData, solarCosts => {
  const rate = solarCosts.lenderRate;
  if (!rate) return "None";
  if (solarCosts.tpo) {
    return rate.code;
  }
  return `${rate.lender.name} - ${rate.term_years}yrs @ ${numeral(rate.apr).format("0.00")}%`;
});

export const getSelectedLoanProductDisplayName = createSelector(getSelectedLenderRate, rate => {
  if (!rate) return "";
  if (rate.lease) return "";
  if (rate.display_name) return rate.display_name;
  return `${numeral(rate.apr).format("0.00")}% APR for ${rate.term_years}yrs*`;
});

export const getLenderFootnote = createSelector(getSolarCostData, solarCosts => solarCosts.lenderFootnote);
export const getFinanceCostDisclaimer = createSelector(getSolarCostData, getLanguage, (solarCosts, language) => {
  const disclaimer = solarCosts.financeCostDisclaimer;
  if (!disclaimer) return undefined;
  if (typeof disclaimer === "string") return disclaimer;
  return (disclaimer[language] ?? disclaimer["en-US"]) as string | undefined;
});

export const getLenderWithDeferredPaymentAllowed = createSelector(
  getSolarCostData,
  solarCosts => !!solarCosts.lenderRate?.customFields?.deferredPaymentAllowed
);

/**
 * Why Awesome Content
 */

export const selectWhyAwesomeSection = createSelector(getSolarCostData, solarCosts => solarCosts.whyAwesomeSection);

/**
 * Chart Data
 */

const getChartData = createSelector(getProposal, proposal => proposal.chartData);

export const getFirstYearProductionData = createSelector(
  [getChartData, getSavingsAnalysis],
  (chartData, savingsAnalysis) => {
    if (savingsAnalysis) {
      return savingsAnalysis.solarFirstYear.map(x => x.qty);
    }

    return chartData.solarProductionFirstYear ?? [];
  }
);

export const getFirstYearConsumptionChartData = createSelector(
  [getChartData, getSavingsAnalysis, getConsumption],
  (chartData, savingsAnalysis, consumption): { value: number | null; type: "actual" | "estimate" }[] => {
    if (consumption) {
      return consumption.estimatedConsumption.map((estimatedValue, index) => {
        const actual = consumption.actualConsumption[index];
        return { value: actual ?? estimatedValue, type: !isNil(actual) ? "actual" : "estimate" };
      });
    }

    if (savingsAnalysis) {
      return savingsAnalysis.utilityBeforeSolarFirstYear.map(x => ({ value: x.qty, type: "estimate" }));
    }

    return (chartData.consumptionFirstYear ?? []).map(value => ({ value, type: "estimate" }));
  }
);

export const getFirstYearConsumptionData = createSelector([getFirstYearConsumptionChartData], firstYearConsumption => {
  return firstYearConsumption.map(month => month.value ?? 0);
});

export const getLocalUtilityRatesData = createSelector(
  [getChartData, getSavingsAnalysis],
  (chartData, savingsAnalysis) => {
    if (savingsAnalysis) {
      return savingsAnalysis.utilityBeforeSolarAnnual.map(x => round(x.cost, 2));
    }

    return chartData.localUtilityRates ?? [];
  }
);

export const getSolarRatesData = createSelector([getChartData, getSavingsAnalysis], (chartData, savingsAnalysis) => {
  if (savingsAnalysis) {
    return savingsAnalysis.solarAnnual.map(x => x.cost);
  }

  return chartData.solarRates ?? [];
});

export const getNationalUtilityRatesData = createSelector(getChartData, chartData => chartData.nationalUtilityRates);

export const getPastUtilityData = createSelector(getChartData, chartData => chartData.pastUtilityData);
export const getPast30YearUtilityRates = createSelector(getPastUtilityData, x => x.prev30YearRates);
export const getYearsInHome = createSelector(getPastUtilityData, x => x.yearsInHome || 10);

/**
 * Deck
 */

const getDeckState = (state: RootState) => state.deck;

export const selectDeckFeatureFlags = createSelector(getDeckState, deck => deck.features);
export const getDeckReady = createSelector(getDeckState, deck => deck.ready);
export const getCurrentSlideId = createSelector(getDeckState, deck => deck.currentSlideId);
export const getIsVerticalNavigation = createSelector(getDeckState, deck => deck.options.view === "scroll");

/**
 * Feature Flags
 */

export const getShowArrayDataPopup = createSelector(
  [getHasArrayData, getIsSoloDesign, getIsScaniflyDesign, getIsSolargrafDesign, getArrayData],
  (hasArrayData, isSoloDesign, isScaniflyDesign, isSolargrafDesign, arrayData) => {
    return (
      hasArrayData &&
      !isSoloDesign &&
      !isScaniflyDesign &&
      !isSolargrafDesign &&
      // If no arrays have production no need to show. Right now only PvWatts gets us this, but some day Aurora might as well
      arrayData?.some(array => !!array.active_panel_count && array.active_panel_production > 0)
    );
  }
);

export const getShowVoluntaryPrepaymentMessage = createSelector(
  [getSolarCostData],
  solarCosts => solarCosts.showVoluntaryPrepaymentMessage ?? true
);

export const getReamortizationMonth = createSelector([getSolarCostData], solarCosts => {
  if (solarCosts.monthlyPaymentSchedule) {
    return solarCosts.monthlyPaymentSchedule.reamortization_month
      ? solarCosts.monthlyPaymentSchedule.reamortization_month
      : solarCosts.monthlyPaymentSchedule.intro_period + 1;
  } else if (solarCosts.introPeriodMonths) {
    return solarCosts.introPeriodMonths + 1;
  }

  return 0;
});

/**
 * Deal states to notify user about
 */

export const getMissingLenderRate = createSelector([getSolarCostData], solarCosts => !solarCosts.lenderRate);

/**
 * Modals / Dialogs
 */

export const getDialogStack = (state: RootState) => state.dialogs.stack;

/**
 * Chart Derivatives
 */

const getExactFirstYearMonthlyBills = createSelector(
  [getChartData, getSavingsAnalysis],
  (chartData, savingsAnalysis) => {
    if (savingsAnalysis) {
      return savingsAnalysis.utilityBeforeSolarFirstYear.map(x => x.cost);
    }
    return chartData.exactFirstYearMonthlyBills;
  }
);

const getChartProportionsByBill = createSelector([getExactFirstYearMonthlyBills], firstYearMonthlyBills => {
  const total = sum(firstYearMonthlyBills);
  return (firstYearMonthlyBills ?? [])?.map(month => month / total);
});

export const getFirstYearChartData = createSelector(
  [getLocalUtilityRatesData, getChartProportionsByBill, getExactFirstYearMonthlyBills],
  (utilityRates, proportions, exactFirstYearMonthlyBills) => {
    if (exactFirstYearMonthlyBills) {
      return exactFirstYearMonthlyBills;
    }
    const annualRate = utilityRates[0];
    return proportions.map(factor => annualRate * factor);
  }
);

export const getSelectedYearRateFactory = createSelector(
  [getLocalUtilityRatesData, getChartProportionsByBill, getExactFirstYearMonthlyBills],
  (utilityRates, proportions, exactFirstYearMonthlyBills) => {
    return (year: number) => {
      if (year === 0 && !!exactFirstYearMonthlyBills) {
        return exactFirstYearMonthlyBills;
      }
      const annualRate = utilityRates[year];
      return proportions.map(factor => annualRate * factor);
    };
  }
);

/*
 * Draw Mode
 */

const getDrawState = (state: RootState) => state.drawMode;

export const getDrawBoards = createSelector([getDrawState], drawState => drawState?.boards);
export const getActiveBoard = createSelector([getDrawState], drawState => drawState?.activeBoardId);

/*
 * Content
 */

export const getFaq = createSelector([getTemplate], templateState => templateState.faq);
export const getAdditionalPricingDisclaimers = createSelector(
  [getTemplate],
  templateState => templateState.additionalPricingDisclaimers ?? []
);

/**
 * Aurora V2
 */

const getAuroraV2 = (state: RootState) => state.auroraV2;

export const getIsAuroraV2Design = createSelector(
  [getProposal, getIsV2Deal],
  (proposal, isV2Deal) => proposal.design.tool === "aurora" && isV2Deal
);

/** Check if design is using any `aurora` variant as a design tool (ie AuroraDTP) */
export const getIsUsingAuroraTool = createSelector(getProposal, proposal => {
  return proposal.design.tool.toLowerCase().startsWith("aurora");
});

export const getIsAuroraDTPDesign = createSelector([getProposal], proposal => proposal.design.tool === "AuroraDTP");

export const getAuroraV2Abilities = createSelector(getAuroraV2, aurora => aurora.abilities);
export const getCanViewDesignLink = createSelector(
  getAuroraV2Abilities,
  abilities => !!abilities.canViewExternalDesign
);
export const getCanViewSalesModeLink = createSelector(
  getAuroraV2Abilities,
  abilities => !!abilities.canViewExternalProposal
);

export const getAuroraV2DesignUrl = createSelector([getAuroraV2, getCanViewDesignLink], (aurora, canViewDesignLink) =>
  canViewDesignLink ? aurora.externalDesignToolLink : undefined
);
export const getAuroraV2SalesModeUrl = createSelector(
  [getAuroraV2, getCanViewSalesModeLink],
  (aurora, canViewSalesModeLink) =>
    canViewSalesModeLink ? `${aurora.externalDesignToolLink}/e-proposal/design` : undefined
);

/*
 * Solo
 */

const getSolo = (state: RootState) => state.solo;
export const getSoloProposalUrl = createSelector(getSolo, solo => solo.soloProposalUrl);
export const getIsSoloProposalLoaded = createSelector(getSolo, solo => solo.iframeLoaded);

/**
 * Proposal Comparison
 */

export const getProposalsToCompare = createSelector(getProposal, proposal => proposal.compareProposals ?? []);

/**
 * Savings
 */

export const selectLifetimeYears = createSelector([getSavingsAnalysis], savingsAnalysis => {
  return savingsAnalysis?.summary.lifetimeYears ?? 30;
});

export const selectCostOfDoingNothing = createSelector(
  [getChartData, getSavingsAnalysis],
  (chartData, savingsAnalysis) => {
    if (savingsAnalysis) {
      return sum(savingsAnalysis.utilityBeforeSolarAnnual.map(x => x.cost));
    }

    if (chartData.compoundingUtilityRates) {
      return last(chartData.compoundingUtilityRates) ?? 0;
    }

    return 0;
  }
);

export const selectBeforeSolarUtilityRates = createSelector([getSavingsAnalysis], savingsAnalysis => {
  return savingsAnalysis?.utilityBeforeSolarAnnual.map(year => year.rate);
});

export const selectStartingUtilRate = createSelector([selectBeforeSolarUtilityRates], rates => {
  if (rates && rates.length) {
    return rates[0];
  }
  return 0;
});

export const selectEndingUtilRate = createSelector([selectBeforeSolarUtilityRates], rates => {
  if (rates && rates.length) {
    return rates[rates.length - 1];
  }
  return 0;
});

export const selectLifetimeAverageUtilRate = createSelector([selectBeforeSolarUtilityRates], rates => {
  if (rates && rates.length) {
    return sum(rates) / rates.length;
  }
  return 0;
});

export const selectAnnualSolarRates = createSelector([getSavingsAnalysis], savingsAnalysis => {
  return savingsAnalysis?.solarAnnual.map(year => year.rate);
});

export const selectStartingSolarRate = createSelector([selectAnnualSolarRates], rates => {
  if (rates && rates.length) {
    return rates[0];
  }
  return 0;
});

export const selectAnnualUtilityBeforeSolarCostData = createSelector(
  [getChartData, getSavingsAnalysis],
  (chartData, savingsAnalysis) => {
    if (savingsAnalysis) {
      return savingsAnalysis.utilityBeforeSolarAnnual.map(x => x.cost);
    }

    return chartData.localUtilityRates?.map(rate => round(rate, 2)) ?? [];
  }
);

export const selectAverageMonthlyUtilityBeforeSolarCostData = createSelector(
  [selectAnnualUtilityBeforeSolarCostData],
  annualUtilityBeforeSolarCostData => annualUtilityBeforeSolarCostData.map(annual => annual / 12)
);

export const selectLifetimeAverageMonthlyUtilityBillBeforeSolar = createSelector(
  [getSavingsAnalysis],
  savingsAnalysis => {
    return savingsAnalysis
      ? savingsAnalysis.summary.lifetimeUtilityBeforeCost / savingsAnalysis.summary.lifetimeYears / 12
      : 0;
  }
);

export const getVariableAnnualPaymentSchedule = createSelector([getSolarCostData], solarCosts => {
  return solarCosts.annualPayments;
});

export const selectAnnualSolarCostData = createSelector(
  [getLoanTermYears, getSolarMonthlyPayment, getVariableAnnualPaymentSchedule],
  (years, monthlyPayment, variableAnnualPaymentSchedule) => {
    if (variableAnnualPaymentSchedule) {
      return variableAnnualPaymentSchedule;
    }
    return range(0, years ?? 0, 1).map(() => round(monthlyPayment * 12, 2));
  }
);

export const selectLifetimeSolarCost = createSelector(
  [selectAnnualSolarCostData, getNetSystemCost],
  (annualSolarCosts, netSystemCost) => {
    const lifetimeSolarCost = sum(annualSolarCosts);
    if (lifetimeSolarCost > 0) {
      return lifetimeSolarCost;
    }

    // lifetime solar cost for cash deals === net system cost
    return netSystemCost ?? 0;
  }
);

export const selectLifetimeAverageMonthlySolarCost = createSelector(
  [selectLifetimeSolarCost, selectLifetimeYears],
  (lifetimeCost, lifetimeYears) => lifetimeCost / lifetimeYears / 12
);

export const selectAnnualUtilityAfterSolarCostData = createSelector(
  [getChartData, getSavingsAnalysis],
  (chartData, savingsAnalysis) => {
    if (savingsAnalysis) {
      return savingsAnalysis.utilityAfterSolarAnnual.map(x => x.cost);
    }

    return chartData.utilityCostWithOffset ?? [];
  }
);

export const selectLifetimeUtilityAfterSolarCost = createSelector(
  [selectAnnualUtilityAfterSolarCostData],
  annualUtilityAfterSolarCostData => {
    const lifetimeUtilityAfterSolarCost = sum(annualUtilityAfterSolarCostData);
    return lifetimeUtilityAfterSolarCost;
  }
);

export const selectTotalLifetimeSavings = createSelector(
  [selectCostOfDoingNothing, selectLifetimeSolarCost, selectLifetimeUtilityAfterSolarCost, getSavingsAnalysis],
  (costOfDoingNothing, lifetimeSolarCost, lifetimeUtilityAfterSolarCost, savingsAnalysis) => {
    if (savingsAnalysis) {
      const lifetimeAvoidedCost = savingsAnalysis.summary.lifetimeAvoidedCost;
      return lifetimeAvoidedCost;
    }

    const totalSavings = costOfDoingNothing - (lifetimeSolarCost + lifetimeUtilityAfterSolarCost);
    return totalSavings;
  }
);

export const selectAnnualSavings = createSelector(
  [getSavingsAnalysis],
  savingsAnalysis => savingsAnalysis?.solarSavingsAnnual
);

export const selectAverageMonthlySavings = createSelector(
  [getSavingsAnalysis],
  savingsAnalysis =>
    savingsAnalysis?.summary && savingsAnalysis.summary.lifetimeAvoidedCost / savingsAnalysis.summary.lifetimeYears / 12
);

export const selectAverageMonthlyUtilityPayment = createSelector([getUtilityCosts], utilityCosts => {
  return parseFloat(`${utilityCosts.monthlyPayment}`) || 0;
});

export const selectAverageMonthlyUtilityBillAfterSolar = createSelector(
  [getSolarOffset, selectAverageMonthlyUtilityPayment, getSavingsAnalysis],
  (solarOffset, monthlyUtilityPayment, savingsAnalysis) => {
    if (savingsAnalysis) {
      return (
        savingsAnalysis.summary.firstYearUtilityAfterAverageBill ??
        sum(savingsAnalysis.utilityAfterSolarFirstYear.map(x => x.cost)) / 12
      );
    }

    return Math.max(0, ((100 - solarOffset) * monthlyUtilityPayment) / 100);
  }
);

export const selectLifetimeAverageMonthlyUtilityBillAfterSolar = createSelector(
  [getSavingsAnalysis],
  savingsAnalysis => {
    return savingsAnalysis
      ? savingsAnalysis.summary.lifetimeUtilityAfterCost / savingsAnalysis.summary.lifetimeYears / 12
      : 0;
  }
);

export const getCanShowSolarSavings = createSelector([getSolarCostData], solarCosts => {
  if (!solarCosts.canShowSolarSavings) return false;
  return true;
});

export const getShowStandardDisclaimer = createSelector(
  getCurrentFinanceInfo,
  getIsTpo,
  getSolarCostData,
  (currentLenderInfo, isTpo, solarCosts) => {
    if (currentLenderInfo.isCash || isTpo || solarCosts.hideStandardDisclaimer) {
      return false;
    }

    return true;
  }
);
